<template>
    <div class="tab-slider__box">
        <div class="tab-slider__panel">
            <div class="tab-slider__panel--wrap">
                <div ref="tabBar" class="mdc-tab-bar" role="tablist">
                    <div class="mdc-tab-scroller">
                        <div class="mdc-tab-scroller__scroll-area">
                            <div class="mdc-tab-scroller__scroll-content">
                                <button
                                    v-for="(slot, i) in slots"
                                    :class="tabClass(i)"
                                    role="tab"
                                    :aria-selected="i===0?'true':'false'"
                                    :tabindex="i"
                                    @click="setActiveItem(i)"
                                >
                                    <span class="mdc-tab__content">
                                        <span class="mdc-tab__text-label" v-text="slot.props.label" />
                                    </span>
                                    <span :class="indicatorClass(i)">
                                        <span class="mdc-tab-indicator__content mdc-tab-indicator__content--underline"></span>
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="tab-slider__border"></div>
        </div>
        <div class="tab-slider__content">
            <div ref="wrap" class="tab-slider__content--wrap">
                <div ref="items" class="tab-slider__target" v-for="(slot, i) in slots" :class="{active: i === 0}">
                    <render-vnode :v-node="slot" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import {onMounted, ref, useSlots} from "vue";
import {waitForElementTransition} from "@/utils";
import {MDCTabBar} from '@material/tab-bar';

//const tabBar = new MDCTabBar(document.querySelector('.mdc-tab-bar'));

export default {
    name: "tab-slider",
    setup() {
        // Get slots for render as content
        const slots = useSlots().default();
        const items = ref([]);
        const wrap = ref();
        const current = ref(0);
        const tabBar = ref();
        const tabBarObj = ref();

        const setActiveItem = (index) => {
            if (current.value !== index) {
                const curEl = items.value[current.value];
                const nextEl = items.value[index];
                const cur = curEl.children[0].offsetHeight;
                const next = nextEl.children[0].offsetHeight;
                wrap.value.style.height = cur + "px";
                curEl.classList.remove("active")
                curEl.classList.add("close");
                nextEl.classList.add("active");
                if (current.value > index) {
                    curEl.classList.add("tab-slider-next-out");
                    nextEl.classList.add("tab-slider-prev-in");
                } else {
                    curEl.classList.add("tab-slider-prev-out");
                    nextEl.classList.add("tab-slider-next-in");
                }

                current.value = index;
                setTimeout(() => {
                    wrap.value.style.height = next + "px";
                    waitForElementTransition(wrap.value).then(() => {
                        wrap.value.style.height = null;
                        nextEl.classList.remove(
                            'close',
                            "tab-slider-prev-in",
                            "tab-slider-prev-out",
                            "tab-slider-next-in",
                            "tab-slider-next-out"
                        );
                        curEl.classList.remove(
                            'close',
                            "tab-slider-prev-in",
                            "tab-slider-prev-out",
                            "tab-slider-next-in",
                            "tab-slider-next-out"
                        );
                    });
                },10)
            }
        }

        const tabClass = i => {
            return i?'mdc-tab':'mdc-tab mdc-tab--active';
        }

        const indicatorClass = i => {
            return i?'mdc-tab-indicator':'mdc-tab-indicator mdc-tab-indicator--active';
        }

        onMounted(() => {
            tabBarObj.value = new MDCTabBar(tabBar.value);
        });

        return {
            slots,
            tabBar,
            wrap,
            items,
            current,
            tabClass,
            indicatorClass,
            setActiveItem
        }
    }
}
</script>

<style lang="scss">
@use "@/scss/design-library/_typography.scss";
@use "@/scss/design-library/_colors.scss";
@use "@/scss/design-library/grid.scss";

$tab-slider-transition-duration: .5s;

.tab-slider {
    &__panel {
        position: relative;

        &--wrap {
            position: relative;
            overflow-x: auto;
            overflow-y: visible;
            margin-bottom: 24px;
            z-index: 10;
        }
    }

    &__tabs {
        display: flex;
        flex-direction: row;
        height: 50px;
        width: auto;
        min-width: 100%;
    }

    &__border {
        position: relative;
        background-color: #{colors.$grey};
        z-index: 0;
    }

    &__button {
        @include typography.body2();
        font-weight: 530;
        cursor: pointer;
        color: #{colors.$stone-grey};
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        transition: all ease-out #{$tab-slider-transition-duration};

        margin: 0;
        padding: 0 16px;

        background-color: transparent;
        border: 0;
        width: 100%;
        height: 100%;
        text-align: center;

        &.active {
            color: #{colors.$highlight-blue};
        }
    }

    &__border {
        position: absolute;
        left:0;
        bottom: 0;
        width: 100%;
        height: 2px;
        background-color: #{colors.$grey};
        z-index: 5;
    }

    &__content {
        overflow: hidden;
        position: relative;
        &--wrap {
            transition: all ease-out #{$tab-slider-transition-duration};
        }
    }

    &__target {
        width: 100%;
        overflow: hidden;
        height: 0;
        margin: -1px;

        position: absolute;
        top: 0;
        left: 0;
        z-index: 5;

        animation-duration: #{$tab-slider-transition-duration};
        animation-timing-function: ease-out;
        opacity: 0;

        > * {
            border: 1px solid transparent;
        }

        &.close {
            height: unset;
        }

        &.active {
            position: relative;
            z-index: 10;
            opacity: 1;
            height: unset;
        }
    }

    &__item {
        border: 1px solid transparent;
        @include typography.body-text();
    }

    &-prev {
        &-in {
            animation-name: tab-slider-prev-in;
        }
        &-out {
            animation-name: tab-slider-prev-out;
        }
    }

    &-next {
        &-in {
            animation-name: tab-slider-next-in;
        }
        &-out {
            animation-name: tab-slider-next-out;
        }
    }

    @keyframes tab-slider-next-in  {
        from {transform: translateX(100%)}
        to {transform: translateX(0%)}
    }
    @keyframes tab-slider-next-out  {
        from {
            transform: translateX(0%);
            opacity: 1;
        }
        to {
            transform: translateX(100%);
            opacity: 0;
        }
    }
    @keyframes tab-slider-prev-in  {
        from {transform: translateX(-100%)}
        to {transform: translateX(0%)}
    }
    @keyframes tab-slider-prev-out  {
        from {
            transform: translateX(0%);
            opacity: 1;
        }
        to {
            transform: translateX(-100%);
            opacity: 0;
        }
    }
}

</style>
